import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { buildWorkspaceNavigationItems } from "../lib/workspace-navigation";
import type { WorkspaceNavigationAccess } from "../lib/workspace-navigation";

const MOBILE_MENU_ID = "mobile-public-nav";

export interface PublicHeaderNavItem {
  id: string;
  label: string;
  path?: string;
  action?: "logout";
  emphasized?: boolean;
  primary?: boolean;
}

export interface PublicHeaderAccess extends WorkspaceNavigationAccess {}

export interface PublicHeaderState {
  open: boolean;
}

export type PublicHeaderAction =
  | { type: "toggle" }
  | { type: "close" }
  | { type: "navigate" }
  | { type: "keydown"; key: string };

export function publicHeaderReducer(state: PublicHeaderState, action: PublicHeaderAction): PublicHeaderState {
  if (action.type === "toggle") return { open: !state.open };
  if (action.type === "close" || action.type === "navigate") return { open: false };
  if (action.type === "keydown" && action.key === "Escape") return { open: false };
  return state;
}

export function buildPublicNavItems(): PublicHeaderNavItem[] {
  return [
    { id: "events", label: "Events", path: "/tickets" },
    { id: "previous", label: "Previous Events", path: "/tickets/previous" },
  ];
}

export function buildAccountMenuItems(options: PublicHeaderAccess): PublicHeaderNavItem[] {
  if (!options.auth) return [];
  const items: PublicHeaderNavItem[] = [...buildWorkspaceNavigationItems(options)];
  items.push({ id: "logout", label: "Logout", action: "logout", emphasized: true });

  return items;
}

const buyTicketsItem: PublicHeaderNavItem = { id: "buy", label: "Buy Tickets", path: "/tickets", primary: true };
const loginItem: PublicHeaderNavItem = { id: "login", label: "Login", path: "/login", emphasized: true };

export function buildMobileNavItems(options: PublicHeaderAccess): PublicHeaderNavItem[] {
  return [...buildPublicNavItems(), buyTicketsItem, ...(options.auth ? buildAccountMenuItems(options) : [loginItem])];
}

interface PublicHeaderProps extends PublicHeaderAccess {
  hidden: boolean;
  locationKey: string;
  navigate: (path: string) => void;
  onLogout: () => Promise<void>;
}

function pathIsActive(currentPath: string, itemPath?: string) {
  if (!itemPath) return false;
  if (itemPath === "/tickets") return currentPath === "/tickets" || currentPath === "/events";
  if (itemPath === "/app/events") return currentPath === "/app/events" || currentPath.startsWith("/app/events/");
  if (itemPath === "/owner") return currentPath === "/owner" || currentPath.startsWith("/owner/");
  return currentPath === itemPath;
}

export default function PublicHeader({
  auth,
  canUseOrganizerDashboard,
  canUseScanner,
  isOwner,
  hidden,
  locationKey,
  navigate,
  onLogout,
}: PublicHeaderProps) {
  const [menuState, dispatch] = useReducer(publicHeaderReducer, { open: false });
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const shellRef = useRef<HTMLElement | null>(null);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const publicNavItems = useMemo(buildPublicNavItems, []);
  const accountMenuItems = useMemo(
    () => buildAccountMenuItems({ auth, canUseOrganizerDashboard, canUseScanner, isOwner }),
    [auth, canUseOrganizerDashboard, canUseScanner, isOwner],
  );
  const mobileNavItems = useMemo(
    () => buildMobileNavItems({ auth, canUseOrganizerDashboard, canUseScanner, isOwner }),
    [auth, canUseOrganizerDashboard, canUseScanner, isOwner],
  );

  useEffect(() => {
    dispatch({ type: "navigate" });
    setAccountMenuOpen(false);
  }, [locationKey]);

  useEffect(() => {
    if (!menuState.open && !accountMenuOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      if (accountMenuOpen && !accountMenuRef.current?.contains(event.target as Node)) setAccountMenuOpen(false);
      if (menuState.open && !shellRef.current?.contains(event.target as Node)) dispatch({ type: "close" });
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      dispatch({ type: "keydown", key: event.key });
      if (event.key === "Escape") setAccountMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [accountMenuOpen, menuState.open]);

  function closeMenus() {
    dispatch({ type: "close" });
    setAccountMenuOpen(false);
  }

  function toggleAccountMenu() {
    dispatch({ type: "close" });
    setAccountMenuOpen((open) => !open);
  }

  function toggleMobileMenu() {
    setAccountMenuOpen(false);
    dispatch({ type: "toggle" });
  }

  async function handleSelect(item: PublicHeaderNavItem) {
    closeMenus();
    if (item.action === "logout") {
      await onLogout();
      return;
    }
    if (item.path) navigate(item.path);
  }

  return (
    <header className={`site-header${hidden ? " site-header--hidden" : ""}`} ref={shellRef}>
      <div className="site-header__inner">
        <div className="site-header__brand-actions">
          <button className="brand-mark" onClick={() => navigate("/")} aria-label="AnTix home">
            <span className="brand-logo-lockup">
              <span className="brand-logo-frame brand-logo-frame-mascot">
                <img className="brand-logo brand-logo-mascot" src="/Antix_logo_2.png" alt="" aria-hidden="true" />
              </span>
              <span className="brand-logo-frame brand-logo-frame-wordmark">
                <img className="brand-logo brand-logo-wordmark" src="/antix_logo_3.png" alt="AnTix" />
              </span>
            </span>
          </button>
          <button onClick={() => void handleSelect(buyTicketsItem)} className="nav-buy-cta" type="button">
            <span className="nav-ticket-icon" aria-hidden="true" />
            {buyTicketsItem.label}
          </button>
        </div>

        <nav className="top-nav" aria-label="Primary navigation">
          <div className="top-nav__public-links">
            {publicNavItems.map((item) => (
              <button
                key={item.id}
                onClick={() => void handleSelect(item)}
                className={pathIsActive(locationKey, item.path) ? "top-nav__item-active" : undefined}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        <div className="site-header__account-actions">
          {auth ? (
            <div className="account-menu-wrap" ref={accountMenuRef}>
              <button
                type="button"
                className="account-menu-toggle"
                aria-label="Account menu"
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
                onClick={toggleAccountMenu}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M4.75 20a7.25 7.25 0 0 1 14.5 0" />
                </svg>
              </button>

              {accountMenuOpen && (
                <div className="account-menu" role="menu">
                  {accountMenuItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      className={`account-menu__item${item.action === "logout" ? " account-menu__item--logout" : ""}${pathIsActive(locationKey, item.path) ? " account-menu__item--active" : ""}`}
                      onClick={() => void handleSelect(item)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <button onClick={() => void handleSelect(loginItem)} className="nav-cta" type="button">
              {loginItem.label}
            </button>
          )}
        </div>

        <button
          type="button"
          className={`menu-toggle${menuState.open ? " menu-toggle--open" : ""}`}
          aria-label={menuState.open ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={menuState.open}
          aria-controls={MOBILE_MENU_ID}
          onClick={toggleMobileMenu}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      <nav id={MOBILE_MENU_ID} className={`mobile-nav${menuState.open ? " mobile-nav--open" : ""}`} aria-label="Mobile navigation" hidden={!menuState.open}>
        {mobileNavItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`mobile-nav__item${item.emphasized ? " mobile-nav__item-emphasized" : ""}${item.primary ? " mobile-nav__item-primary" : ""}${pathIsActive(locationKey, item.path) ? " mobile-nav__item-active" : ""}`}
            onClick={() => void handleSelect(item)}
          >
            {item.primary && <span className="nav-ticket-icon" aria-hidden="true" />}
            {item.label}
          </button>
        ))}
      </nav>
    </header>
  );
}
